<template>
  <section class="wallet-panel section-card">
    <header class="section-card__header">
      <div>
        <h2 class="section-card__title">
          <i class="fa-regular fa-wallet section-card__icon" />
          {{ t('billing.subscriptionPage.wallet.title') }}
        </h2>
        <p class="section-card__subtitle">
          {{ t('billing.subscriptionPage.wallet.subtitle') }}
        </p>
      </div>
      <Button
        :label="t('billing.subscriptionPage.wallet.actions.buy')"
        icon="fa-regular fa-plus"
        class="p-button p-button-sm wallet-panel__primary"
        @click="$emit('buy-credits')"
      />
    </header>

    <div class="wallet-panel__balance">
      <div class="wallet-panel__balance-amount">
        {{ wallet.balance.label }}
      </div>
      <div class="wallet-panel__balance-breakdown">
        <div class="wallet-panel__pill wallet-panel__pill--included">
          <span class="wallet-panel__pill-label">
            {{ t('billing.subscriptionPage.wallet.included') }}
          </span>
          <span class="wallet-panel__pill-value">
            {{ wallet.balance.breakdown.included.label }}
          </span>
        </div>
        <div class="wallet-panel__pill wallet-panel__pill--purchased">
          <span class="wallet-panel__pill-label">
            {{ t('billing.subscriptionPage.wallet.purchased') }}
          </span>
          <span class="wallet-panel__pill-value">
            {{ wallet.balance.breakdown.purchased.label }}
          </span>
        </div>
      </div>

      <!-- Discount on the in-plan SMS pack (discount.plan_sms) -->
      <div
        v-if="wallet.includedSmsDiscountPercent"
        class="wallet-panel__sms-discount"
      >
        <i class="fa-solid fa-tag wallet-panel__sms-discount-icon" />
        <span>
          {{ t('billing.subscriptionPage.wallet.includedSmsDiscount', {
            percent: wallet.includedSmsDiscountPercent,
          }) }}
        </span>
      </div>

      <!-- Next cycle reset date — only shown when the shop has SMS included
           in its subscription (otherwise the date has no meaning for the
           wallet view). Derived from `currentPlanLimit` server-side. -->
      <div
        v-if="includedResetLabel"
        class="wallet-panel__reset-caption"
      >
        <i class="fa-regular fa-arrows-rotate" />
        <span>
          {{ t('billing.subscriptionPage.wallet.includedResetAt', { date: includedResetLabel }) }}
        </span>
      </div>
    </div>

    <div
      v-if="wallet.equivalents.length > 0"
      class="wallet-panel__equivalents"
    >
      <div class="wallet-panel__equivalents-title">
        {{ t('billing.subscriptionPage.wallet.equivalents') }}
      </div>
      <div class="wallet-panel__equivalents-grid">
        <div
          v-for="(eq, i) in wallet.equivalents"
          :key="`${eq.action}-${eq.country}-${i}`"
          class="wallet-panel__equivalent"
        >
          <div class="wallet-panel__equivalent-qty">
            {{ formatQuantity(eq.approxQuantity) }}
          </div>
          <div class="wallet-panel__equivalent-meta">
            {{ eq.label.replace(/^~\s*[\d\s,]+/, '') }}
          </div>
          <div class="wallet-panel__equivalent-price">
            {{ formatPrice(eq.unitPrice) }} / {{ unitLabel(eq.action) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Auto-recharge: hidden when shop has never bought SMS credits
         (`wallet.autoRecharge === null`). Self-managed with confirmation
         modals on toggle/edit — no auto-save. -->
    <div class="wallet-panel__autorecharge">
      <AutoRechargeBlock :auto-recharge="wallet.autoRecharge" />
    </div>
  </section>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import AutoRechargeBlock from '@/components/billing/panels/AutoRechargeBlock.vue';
import { WalletSection } from '@/types/billing-types';

export default defineComponent({
  name: 'WalletPanel',

  components: { Button, AutoRechargeBlock },

  props: {
    wallet: {
      type: Object as PropType<WalletSection>,
      required: true,
    },
  },

  emits: ['buy-credits'],

  setup(props) {
    const { t } = useI18n();

    const currencySymbol = computed(() => (
      props.wallet.balance.currency === 'USD' ? '$' : '€'
    ));

    const formatQuantity = (n: number) => (
      new Intl.NumberFormat('fr-FR').format(n)
    );

    const formatPrice = (n: number) => {
      const trimmed = n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
      return `${trimmed} ${currencySymbol.value}`;
    };

    const unitLabel = (action: 'sms' | 'whatsapp') => (
      action === 'whatsapp' ? t('billing.subscriptionPage.wallet.unit.whatsapp') : 'SMS'
    );

    // Date de renouvellement du cycle d'abonnement, affichée uniquement quand
    // le shop a effectivement des SMS inclus (sinon la date n'a pas de sens
    // ici). Source : `currentPlanLimit` exposé via `breakdown.included.resetAt`.
    // Format : "5 juin 2026" via Intl.DateTimeFormat, fallback brut.
    const includedResetLabel = computed(() => {
      const breakdown = props.wallet.balance.breakdown.included;
      if (!breakdown?.resetAt || breakdown.amount <= 0) return '';
      const date = new Date(breakdown.resetAt);
      if (Number.isNaN(date.getTime())) return breakdown.resetAt;
      return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    });

    return {
      t,
      formatQuantity,
      formatPrice,
      unitLabel,
      includedResetLabel,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.wallet-panel {
  &__primary {
    background: $brand-color-primary !important;
    border-color: $brand-color-primary !important;
    color: #fff !important;
    font-weight: 600 !important;

    &:hover {
      background: #7aae26 !important;
      border-color: #7aae26 !important;
    }
  }

  &__balance {
    margin: 16px 0 24px;
  }

  &__balance-amount {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  &__balance-breakdown {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 12px;
  }

  &__sms-discount {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 0.75rem;
    font-weight: 600;
    background: rgba(148, 200, 64, 0.12);
    color: #629d03;
  }

  &__sms-discount-icon {
    font-size: 0.6875rem;
  }

  &__reset-caption {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    font-size: 0.75rem;
    color: #64748b;

    i {
      font-size: 0.75rem;
      color: #94a3b8;
    }
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 100px;
    font-size: 0.8125rem;
    background: #f5f6f8;
    border: 1px solid #e5e7eb;

    &--included {
      background: rgba(148, 200, 64, 0.1);
      border-color: rgba(148, 200, 64, 0.3);
    }
  }

  &__pill-label {
    color: #6b7280;
    font-weight: 500;
  }

  &__pill-value {
    color: #1f2937;
    font-weight: 600;
  }

  &__equivalents {
    background: #fbfaf7;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 16px;
  }

  &__equivalents-title {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 12px;
  }

  &__equivalents-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
  }

  &__equivalent {
    background: #fff;
    border-radius: 8px;
    padding: 12px 14px;
    border: 1px solid #eaeaea;
  }

  &__equivalent-qty {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.1;
  }

  &__equivalent-meta {
    font-size: 0.8125rem;
    color: #4b5563;
    margin-top: 2px;
  }

  &__equivalent-price {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-top: 4px;
  }

  &__autorecharge {
    margin-top: 16px;
  }
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
